import { api, handleApiError } from './api';
import { getBusinessIdentifier } from '../utils/auth';

// Based on actual API response structure
export interface SalesPersonDetails {
  details: {
    business_id: string;
    createdAt: string;
    email: string;
    full_name: string;
    is_admin: boolean;
    project_ids: string[];
    type: string;
    updatedAt: string;
    user_id: string;
    __v: number;
    _id: string;
  };
  stats: {
    totalMeetings: number;
    activeClientCount: number;
  };

  // Legacy fields for backward compatibility (optional)
  id?: string | number;
  name?: string;
  email?: string;
  phone?: string;
  assignedProjectsCount?: number;
  assignedProjectNames?: string[];
  totalMeetingsCount?: number;
  activeClientsCount?: number;
  sopAdherence?: number;
  objectionHandling?: number;
  talkToListenRatio?: number;
  projects?: {
    count: string;
    names: string;
  };
  pendingActions?: string;
  totalMeetings?: string;
  activeClients?: string;
}

// Assumed structure for the list of salespersons
export interface SalesPersonBasicInfo {
  id: string | number; // Assuming id is part of the list response
  name: string;
  email: string;
  // any other basic fields returned by the list endpoint
}

export interface SalesPersonListParams {
  type?: string;
  // Add other potential params if needed in the future, e.g., projectId if not auto-added by interceptor
}

class SalesPersonService {
  /**
   * Fetches a list of salespersons.
   * @param params Optional parameters to filter the list, e.g., by type.
   * @returns A promise that resolves to an array of salesperson basic info.
   */
  async listSalesPersons(params?: SalesPersonListParams): Promise<SalesPersonBasicInfo[]> {
    try {
      const response = await api.get<SalesPersonBasicInfo[]>('/sales-person/list', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Fetches detailed overview information for a specific salesperson.
   * This should provide metrics like SOP adherence, objection handling, etc.
   * @param salesPersonId The unique identifier of the salesperson.
   * @returns A promise that resolves to the salesperson's detailed overview.
   */
  async getSalesPersonOverview(salesPersonId: string | number): Promise<SalesPersonDetails> {
    try {
      const business_identifier = getBusinessIdentifier();
      // Using a placeholder for the actual overview data structure, adjust as needed
      const response = await api.get<{success: boolean, data: SalesPersonDetails}>(`/api/sales-person/${salesPersonId}/overview`, {
        params: { business_identifier }
      });
      
      // Extract the actual data from the nested response structure
      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error('Invalid API response structure');
      }
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * Fetches detailed information for a specific salesperson.
   * @param salesExecutiveId The unique identifier of the salesperson.
   * @returns A promise that resolves to the salesperson's details.
   */
  async getSalesPersonDetails(salesExecutiveId: string | number): Promise<SalesPersonDetails> {
    try {
      const business_identifier = getBusinessIdentifier();
      // Ensure the endpoint matches your backend route for salesperson details
      const response = await api.get<{success: boolean, data: SalesPersonDetails}>(`/api/salespersons/${salesExecutiveId}`, {
        params: { business_identifier }
      });
      
      // Extract the actual data from the nested response structure
      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error('Invalid API response structure');
      }
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Potential future methods:
  // async listSalesPersons(params?: any): Promise<SalesPersonDetails[]> { ... }
  // async updateSalesPerson(salesExecutiveId: string | number, updates: Partial<SalesPersonDetails>): Promise<SalesPersonDetails> { ... }
}

export const salesPersonService = new SalesPersonService(); 